import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useNotificacoes from '../hooks/useNotificacoes';
import api from '../services/api';
import {
  LayoutDashboard, Users, Package, FileText,
  Receipt, DollarSign, Target, LogOut, Menu, X, ChevronRight,
  UserCog, ClipboardList, BarChart2, Building2, Bell, Shield, CreditCard,
  Search, Settings, TrendingUp,
} from 'lucide-react';

const fmtBRL = (v) =>
  (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const diasAtraso = (dataStr) => {
  if (!dataStr) return 0;
  const venc = new Date(dataStr + 'T00:00:00');
  return Math.max(0, Math.floor((new Date() - venc) / 86400000));
};

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const { totalAtrasados, vencimentosAtrasados } = useNotificacoes();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const [alertasClientes, setAlertasClientes] = React.useState([]);

  // ── BUSCA GLOBAL ──────────────────────────────────────
  const [searchOpen, setSearchOpen]       = React.useState(false);
  const [searchQuery, setSearchQuery]     = React.useState('');
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchResults, setSearchResults] = React.useState({ pedidos: [], clientes: [], materiais: [] });
  const searchDataRef  = React.useRef(null);
  const searchInputRef = React.useRef(null);
  const debounceRef    = React.useRef(null);

  React.useEffect(() => {
    if (!isAdmin) return;
    api.get('/dashboard/clientes-sem-compra?dias=60')
      .then(res => setAlertasClientes(Array.isArray(res.data) ? res.data : []))
      .catch(() => { /* silent — sino fica sem badge em caso de falha */ });
  }, [isAdmin]); // eslint-disable-line

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults({ pedidos: [], clientes: [], materiais: [] });
  };

  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => {
          if (!prev) setTimeout(() => searchInputRef.current?.focus(), 50);
          else { setSearchQuery(''); setSearchResults({ pedidos: [], clientes: [], materiais: [] }); }
          return !prev;
        });
      }
      if (e.key === 'Escape') closeSearch();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line

  const highlight = (text, q) => {
    if (!text || !q?.trim()) return text || '';
    const str = String(text);
    const idx = str.toUpperCase().indexOf(q.toUpperCase().trim());
    if (idx === -1) return str;
    const len = q.trim().length;
    return (
      <>
        {str.slice(0, idx)}
        <mark className="bg-yellow-200 text-yellow-900 font-black rounded-sm px-0.5 not-italic">
          {str.slice(idx, idx + len)}
        </mark>
        {str.slice(idx + len)}
      </>
    );
  };

  const doSearch = async (q) => {
    if (!q.trim()) {
      setSearchResults({ pedidos: [], clientes: [], materiais: [] });
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      if (!searchDataRef.current) {
        const [rP, rC, rM] = await Promise.all([
          api.get('/pedidos'),
          api.get('/clientes'),
          api.get('/materiais'),
        ]);
        searchDataRef.current = {
          pedidos:   Array.isArray(rP.data) ? rP.data : [],
          clientes:  Array.isArray(rC.data) ? rC.data : [],
          materiais: Array.isArray(rM.data) ? rM.data : [],
        };
      }
      const qu = q.toUpperCase().trim();
      const d  = searchDataRef.current;
      setSearchResults({
        pedidos: d.pedidos.filter(p =>
          p.numero_fabrica?.toUpperCase().includes(qu) ||
          p.numero_fe?.toUpperCase().includes(qu) ||
          p.cliente_nome?.toUpperCase().includes(qu)
        ).slice(0, 3),
        clientes: d.clientes.filter(c =>
          c.nome?.toUpperCase().includes(qu) ||
          c.nome_fantasia?.toUpperCase().includes(qu) ||
          (c.cnpj || '').replace(/\D/g, '').includes(qu.replace(/\D/g, ''))
        ).slice(0, 3),
        materiais: d.materiais.filter(m =>
          m.numero_fe?.toUpperCase().includes(qu) ||
          m.nome?.toUpperCase().includes(qu) ||
          m.descricao?.toUpperCase().includes(qu)
        ).slice(0, 3),
      });
    } catch { /* ignore */ }
    setSearchLoading(false);
  };

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const totalNotif = totalAtrasados + alertasClientes.length;

  const menuItems = [
    { path: '/dashboard',     icon: LayoutDashboard, label: 'Dashboard',     adminOnly: false, group: 'main' },
    { path: '/clientes',      icon: Users,           label: 'Clientes',      adminOnly: false, group: 'main' },
    { path: '/materiais',     icon: Package,         label: 'Materiais',     adminOnly: false, group: 'main' },
    { path: '/representadas', icon: Building2,       label: 'Representadas', adminOnly: false, group: 'main' },
    { path: '/pedidos',       icon: FileText,        label: 'Pedidos',       adminOnly: false, group: 'main' },
    { path: '/orcamentos',    icon: ClipboardList,   label: 'Orçamentos',    adminOnly: false, group: 'main' },
    { path: '/relatorios',             icon: BarChart2,  label: 'Relatórios',             adminOnly: false, group: 'main' },
    { path: '/inteligencia-comercial', icon: TrendingUp, label: 'Inteligência Comercial', adminOnly: false, group: 'main' },
    { path: '/comissoes',              icon: DollarSign, label: 'Comissões',              adminOnly: false, group: 'main' },
    { path: '/notas-fiscais',                     icon: Receipt,    label: 'Notas Fiscais', adminOnly: true, group: 'admin' },
    { path: '/metas',                             icon: Target,     label: 'Metas',         adminOnly: true, group: 'admin' },
    { path: '/usuarios',                          icon: UserCog,    label: 'Usuários',      adminOnly: true, group: 'admin' },
    { path: '/configuracoes/condicoes-pagamento', icon: CreditCard, label: 'Cond. Pgto.',   adminOnly: true, group: 'admin' },
    { path: '/auditoria',                         icon: Shield,     label: 'Auditoria',     adminOnly: true, group: 'admin' },
    { path: '/configuracoes',                     icon: Settings,   label: 'Configurações', adminOnly: true, group: 'admin' },
  ];

  const filteredMenuItems = menuItems.filter(item => !item.adminOnly || isAdmin || isSuperAdmin);
  const mainItems  = filteredMenuItems.filter(i => i.group === 'main');
  const adminItems = filteredMenuItems.filter(i => i.group === 'admin');

  const handleLogout = () => { logout(); navigate('/login'); };

  const NavItem = ({ item, isMobile = false, badge = 0, gold = false }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const accent = gold ? '#D97706' : '#4A90D9';
    const activeBg = gold ? 'from-amber-900/40 to-amber-900/10' : 'from-[#0A3D73]/80 to-[#0A3D73]/30';
    return (
      <button
        onClick={() => { navigate(item.path); if (isMobile) setSidebarOpen(false); }}
        className={`
          group w-full flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider
          transition-all duration-150 relative overflow-hidden
          ${isActive ? 'text-white' : 'text-slate-400 hover:text-slate-200'}
        `}
      >
        <span
          className="absolute left-0 top-0 bottom-0 w-0.5 transition-all duration-150"
          style={{ background: isActive ? accent : 'transparent' }}
        />
        {isActive && (
          <span className={`absolute inset-0 bg-gradient-to-r ${activeBg}`} />
        )}
        <span
          className="relative z-10 shrink-0 transition-all duration-150"
          style={{ color: isActive ? accent : undefined }}
        >
          <Icon size={13} strokeWidth={2.5} className={!isActive ? 'text-slate-500 group-hover:text-slate-300' : ''} />
        </span>
        <span className="relative z-10 flex-1 text-left">{item.label}</span>
        {badge > 0 ? (
          <span className="relative z-10 ml-auto flex items-center justify-center min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-black rounded-full leading-none">
            {badge > 99 ? '99+' : badge}
          </span>
        ) : isActive ? (
          <ChevronRight size={10} className="relative z-10 shrink-0" style={{ color: accent }} />
        ) : null}
      </button>
    );
  };

  const SidebarContent = ({ isMobile = false }) => (
    <div className="flex flex-col h-full">

      {/* ── LOGO ── */}
      <div className="px-4 pt-3 pb-3 border-b border-white/5">
        <div className="flex items-center gap-2.5 mb-0.5">
          <img src="/logo-repflow-hd.png" alt="RepFlow" className="h-8 w-auto" />
        </div>
        <p className="text-[8px] font-bold text-slate-500 uppercase tracking-[0.2em] mt-1.5">Representação Comercial</p>
      </div>

      {/* ── NAV PRINCIPAL ── */}
      <nav className="flex-1 pt-2 pb-1 overflow-y-auto">
        <p className="px-4 mb-1.5 text-[8px] font-black uppercase tracking-[0.25em] text-slate-600">
          Módulos
        </p>
        <div className="space-y-0.5 px-1">
          {mainItems.map(item => (
            <NavItem
              key={item.path}
              item={item}
              isMobile={isMobile}
              badge={item.path === '/comissoes' ? totalAtrasados : 0}
            />
          ))}
        </div>
        {adminItems.length > 0 && (
          <>
            <div className="mx-4 my-2 border-t border-white/5" />
            <p className="px-4 mb-1.5 text-[8px] font-black uppercase tracking-[0.25em] text-slate-600">
              Administrativo
            </p>
            <div className="space-y-0.5 px-1">
              {adminItems.map(item => (
                <NavItem key={item.path} item={item} isMobile={isMobile} />
              ))}
            </div>
          </>
        )}
        {isSuperAdmin && (
          <>
            <div className="mx-4 my-2 border-t border-amber-900/30" />
            <p className="px-4 mb-1.5 text-[8px] font-black uppercase tracking-[0.25em] text-amber-700/60">
              Superadmin
            </p>
            <div className="space-y-0.5 px-1">
              <NavItem
                item={{ path: '/superadmin', icon: Shield, label: 'Super Admin' }}
                isMobile={isMobile}
                badge={0}
                gold
              />
            </div>
          </>
        )}
      </nav>

      {/* ── FOOTER USUÁRIO ── */}
      <div className="border-t border-white/5 p-3">
        <div className="flex items-center gap-2 px-2 py-2 mb-2 bg-white/3">
          <div className="w-6 h-6 rounded-none bg-[#0A3D73] border border-[#4A90D9]/30 flex items-center justify-center text-[10px] font-black text-white shrink-0">
            {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black text-white truncate leading-none">{user?.nome || 'Usuário'}</p>
            <p className="text-[8px] font-bold uppercase tracking-wider mt-0.5 text-[#4A90D9]">
              {isSuperAdmin ? 'Superadmin' : isAdmin ? 'Administrador' : 'Consultor'}
            </p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="group w-full flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-red-400 transition-all duration-150"
        >
          <LogOut size={11} strokeWidth={2.5} className="shrink-0 group-hover:text-red-400 transition-colors" />
          <span>Sair do Sistema</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#E9EEF2' }}>

      {/* ── SIDEBAR DESKTOP ── */}
      <aside
        className="hidden md:flex md:flex-col w-48 shrink-0 border-r border-white/5"
        style={{ background: 'linear-gradient(180deg, #0d1117 0%, #111827 60%, #0f172a 100%)' }}
      >
        <SidebarContent />
      </aside>

      {/* ── SIDEBAR MOBILE ── */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <aside
            className="absolute left-0 top-0 bottom-0 w-48 flex flex-col shadow-2xl border-r border-white/5"
            style={{ background: 'linear-gradient(180deg, #0d1117 0%, #111827 60%, #0f172a 100%)' }}
          >
            <div className="flex items-center justify-between px-4 py-4 border-b border-white/5">
              <img src="/logo-repflow-hd.png" alt="RepFlow" className="h-8 w-auto" />
              <button onClick={() => setSidebarOpen(false)} className="p-1 hover:bg-white/10 rounded transition-colors">
                <X size={14} className="text-slate-400" />
              </button>
            </div>
            <SidebarContent isMobile={true} />
          </aside>
        </div>
      )}

      {/* ── ÁREA PRINCIPAL ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Header */}
        <header className="shrink-0 bg-white border-b border-slate-200 px-4 h-11 flex items-center shadow-sm">
          <div className="flex items-center justify-between w-full">
            <button
              className="md:hidden p-1.5 hover:bg-slate-100 rounded transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={18} className="text-slate-700" />
            </button>
            <div className="hidden md:flex items-center gap-2">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.25em]">
                Painel de Controle Interno
              </span>
            </div>

            <div className="flex items-center gap-2">

              {/* ── BUSCA GLOBAL ── */}
              <div className="relative">
                {!searchOpen ? (
                  <button
                    onClick={() => { setSearchOpen(true); setTimeout(() => searchInputRef.current?.focus(), 50); }}
                    title="Buscar (Ctrl+K)"
                    className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                  >
                    <Search size={15} strokeWidth={1.5} className="text-slate-400" />
                  </button>
                ) : (
                  <div className="flex items-center gap-2 bg-white border border-[#0A3D73]/30 rounded-lg px-3 h-8 shadow-sm ring-2 ring-[#0A3D73]/10 w-72">
                    {searchLoading
                      ? <div className="w-3 h-3 border-2 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin flex-shrink-0" />
                      : <Search size={13} strokeWidth={1.5} className="text-slate-400 flex-shrink-0" />
                    }
                    <input
                      ref={searchInputRef}
                      value={searchQuery}
                      onChange={e => handleSearchChange(e.target.value)}
                      placeholder="Buscar pedidos, clientes, FEs..."
                      className="flex-1 text-xs font-semibold text-slate-800 outline-none bg-transparent placeholder-slate-400 min-w-0"
                    />
                    <button onClick={closeSearch} className="flex-shrink-0 p-0.5 hover:bg-slate-100 rounded transition-colors">
                      <X size={11} className="text-slate-400" />
                    </button>
                  </div>
                )}

                {/* Dropdown com resultados */}
                {searchOpen && (searchResults.pedidos.length > 0 || searchResults.clientes.length > 0 || searchResults.materiais.length > 0) && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={closeSearch} />
                    <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden">

                      {searchResults.pedidos.length > 0 && (
                        <div>
                          <p className="px-4 py-2 text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-50 border-b border-slate-100">
                            📦 Pedidos
                          </p>
                          {searchResults.pedidos.map(p => (
                            <button key={p.id}
                              onClick={() => { navigate(`/pedidos/editar/${p.id}`); closeSearch(); }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors border-b border-slate-50 text-left"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-black text-slate-800 font-mono">
                                  {highlight(p.numero_fabrica || p.numero_fe, searchQuery)}
                                </p>
                                <p className="text-[10px] text-slate-500 truncate">
                                  {highlight(p.cliente_nome, searchQuery)}
                                  {p.numero_fe && p.numero_fabrica ? <> · {highlight(p.numero_fe, searchQuery)}</> : null}
                                </p>
                              </div>
                              <ChevronRight size={11} className="text-slate-300 flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      )}

                      {searchResults.clientes.length > 0 && (
                        <div>
                          <p className="px-4 py-2 text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-50 border-b border-slate-100">
                            👥 Clientes
                          </p>
                          {searchResults.clientes.map(c => (
                            <button key={c.id}
                              onClick={() => { navigate(`/clientes/${c.id}`); closeSearch(); }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors border-b border-slate-50 text-left"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-black text-slate-800 uppercase">
                                  {highlight(c.nome_fantasia || c.nome, searchQuery)}
                                </p>
                                <p className="text-[10px] text-slate-500">
                                  {highlight(c.cnpj, searchQuery)}
                                </p>
                              </div>
                              <ChevronRight size={11} className="text-slate-300 flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      )}

                      {searchResults.materiais.length > 0 && (
                        <div>
                          <p className="px-4 py-2 text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-50 border-b border-slate-100">
                            🔧 Materiais
                          </p>
                          {searchResults.materiais.map(m => (
                            <button key={m.id}
                              onClick={() => { navigate(`/materiais/editar/${m.id}`); closeSearch(); }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 transition-colors border-b border-slate-50 text-left"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-xs font-black text-slate-800 font-mono">
                                  {highlight(m.numero_fe || m.codigo, searchQuery)}
                                </p>
                                <p className="text-[10px] text-slate-500 truncate">
                                  {highlight(m.nome || m.descricao, searchQuery)}
                                </p>
                              </div>
                              <ChevronRight size={11} className="text-slate-300 flex-shrink-0" />
                            </button>
                          ))}
                        </div>
                      )}

                    </div>
                  </>
                )}

                {/* Sem resultados */}
                {searchOpen && searchQuery.trim().length >= 2 && !searchLoading &&
                  searchResults.pedidos.length === 0 && searchResults.clientes.length === 0 && searchResults.materiais.length === 0 && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={closeSearch} />
                    <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 px-5 py-6 text-center">
                      <Search size={20} className="text-slate-300 mx-auto mb-2" />
                      <p className="text-xs font-bold text-slate-500">Nenhum resultado para</p>
                      <p className="text-xs font-black text-slate-700 mt-0.5">"{searchQuery}"</p>
                    </div>
                  </>
                )}
              </div>

              {/* ── SINO DE NOTIFICAÇÕES — admin only ── */}
              {isAdmin && (
                <div className="relative">
                  <button
                    onClick={() => setNotifOpen(o => !o)}
                    className="relative p-1.5 hover:bg-slate-100 rounded transition-colors"
                    title={totalNotif > 0 ? `${totalNotif} notificação(ões)` : 'Notificações'}
                  >
                    <Bell
                      size={15}
                      strokeWidth={1.5}
                      className={totalNotif > 0 ? 'text-red-500' : 'text-slate-400'}
                    />
                    {totalNotif > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[14px] h-3.5 px-0.5 bg-red-500 text-white text-[8px] font-black rounded-full leading-none">
                        {totalNotif > 99 ? '99+' : totalNotif}
                      </span>
                    )}
                  </button>

                  {/* Dropdown */}
                  {notifOpen && totalNotif > 0 && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden">

                        {/* Cabeçalho dropdown */}
                        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-red-50">
                          <p className="text-xs font-black uppercase tracking-wide text-red-700 flex items-center gap-2">
                            <Bell size={11} />
                            {totalNotif} notificaç{totalNotif !== 1 ? 'ões' : 'ão'}
                          </p>
                          <button
                            onClick={() => setNotifOpen(false)}
                            className="p-0.5 hover:bg-red-100 rounded transition-colors"
                          >
                            <X size={12} className="text-red-400" />
                          </button>
                        </div>

                        {/* Seção vencimentos */}
                        {totalAtrasados > 0 && (
                          <>
                            <p className="px-4 py-2 text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-50 border-b border-slate-100">
                              💰 Vencimentos em atraso
                            </p>
                            <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                              {vencimentosAtrasados.slice(0, 5).map(v => {
                                const dias = diasAtraso(v.data_vencimento);
                                return (
                                  <div key={v.id} className="px-4 py-2.5 hover:bg-slate-50 transition-colors">
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <p className="text-xs font-black text-slate-800">NF {v.numero_nf}</p>
                                        <p className="text-xs font-semibold text-slate-500 truncate">{v.cliente_nome || '---'}</p>
                                      </div>
                                      <div className="text-right shrink-0">
                                        <p className="text-xs font-black text-red-600">R$ {fmtBRL(v.comissao_calculada)}</p>
                                        <p className="text-[9px] font-bold text-red-400 uppercase">{dias}d atraso</p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <button
                              onClick={() => { navigate('/comissoes?status=Atrasado'); setNotifOpen(false); }}
                              className="w-full py-2 text-xs font-black uppercase tracking-wide text-[#0A3D73] hover:bg-blue-50 transition-colors border-t border-slate-100"
                            >
                              Ver todos os atrasados →
                            </button>
                          </>
                        )}

                        {/* Seção clientes inativos */}
                        {alertasClientes.length > 0 && (
                          <>
                            <p className="px-4 py-2 text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-50 border-b border-t border-slate-100">
                              👥 Clientes inativos
                            </p>
                            <div className="max-h-40 overflow-y-auto divide-y divide-slate-100">
                              {alertasClientes.slice(0, 5).map((c) => {
                                const urg = c.dias_sem_compra >= 180
                                  ? { label: 'CRÍTICO', badge: 'bg-red-100 text-red-700 border-red-300' }
                                  : c.dias_sem_compra >= 90
                                  ? { label: 'URGENTE', badge: 'bg-orange-100 text-orange-700 border-orange-300' }
                                  : { label: 'ATENÇÃO', badge: 'bg-amber-100 text-amber-700 border-amber-300' };
                                return (
                                  <button
                                    key={c.cliente_id}
                                    onClick={() => { navigate(`/clientes/${c.cliente_id}`); setNotifOpen(false); }}
                                    className="w-full px-4 py-2.5 hover:bg-slate-50 transition-colors text-left flex items-center justify-between gap-3"
                                  >
                                    <p className="text-xs font-black text-slate-800 truncate">{c.cliente_nome}</p>
                                    <div className="flex items-center gap-1.5 shrink-0">
                                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-black uppercase border rounded-full ${urg.badge}`}>
                                        {urg.label}
                                      </span>
                                      <span className="text-[9px] font-bold text-slate-400">{c.dias_sem_compra}d</span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                            <button
                              onClick={() => { navigate('/clientes?filtro=inativos'); setNotifOpen(false); }}
                              className="w-full py-2 text-xs font-black uppercase tracking-wide text-[#0A3D73] hover:bg-blue-50 transition-colors border-t border-slate-100"
                            >
                              Ver todos os inativos →
                            </button>
                          </>
                        )}

                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="hidden sm:flex flex-col items-end">
                <span className="text-[11px] font-bold text-slate-700 leading-none">{user?.nome}</span>
                <span className="text-[8px] font-black uppercase tracking-wider text-[#0A3D73] mt-0.5">
                  {isSuperAdmin ? 'Superadmin' : isAdmin ? 'Admin' : 'Vendedor'}
                </span>
              </div>
              <div className="w-7 h-7 bg-[#0A3D73] flex items-center justify-center text-white text-[10px] font-black border border-[#0A3D73]/30 shadow-sm">
                {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
              </div>
            </div>
          </div>
        </header>

        {/* Conteúdo */}
        <main className="flex-1 overflow-y-auto bg-[#E9EEF2]">
          <div className="min-h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
